import { useState, useContext, useRef, useEffect, useCallback } from 'react';
import { NavigationContext } from '../../Header';
import { CartContext, ModalContext } from '../../../App';
import type { CartItem } from '../../../App';
import { getTranslation, Language } from '../../../translations';
import { useLanguage } from '../../Header';
import { getLocaleForLanguage } from '../utils';

// Customer interface
export interface Customer {
  id: string;
  personalInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  addressInfo: {
    street: string;
    postalCode: string;
    city: string;
    country: string;
  };
}

// Contact form state interface
export interface ContactFormState {
  name: string;
  email: string;
  phone: string;
  message: string;
  contactPreference: 'email' | 'phone' | string;
}

// Appointment details interface
export interface AppointmentDetails {
  method: string;
  location: string;
  date: string;
  time: string;
  address?: string;
  customerName?: {
    firstName: string;
    lastName: string;
  };
}

// Calendar event status type
export type CalendarEventStatus = 'idle' | 'success' | 'error';

// Hook props interface
export interface UseCartPageProps {
  items: CartItem[];
  onRemoveItem: (id: string) => void;
  onSuccessNavigate?: (orderNumber: string, email?: string, appointmentDetails?: any) => void;
}

// Hook return type
export interface UseCartPageReturn {
  // State
  showCustomerServicePage: boolean;
  showSuccessPage: boolean;
  orderNumber: string;
  selectedCustomer: Customer | null;
  showDeleteModal: boolean;
  deviceToDelete: string | null;
  showContactModal: boolean;
  closingContactModal: boolean;
  contactForm: ContactFormState;
  isSubmittingContact: boolean;
  showContactSuccess: boolean;
  isCreatingCalendarEvent: boolean;
  calendarEventStatus: CalendarEventStatus;
  calendarEventMessage: string;
  appointmentDetails: AppointmentDetails | null;
  currentDeviceIndex: number;

  // Computed values
  hasOpAanvraagItem: boolean;
  total: number;

  // Refs
  pageTopRef: React.RefObject<HTMLDivElement>;

  // Context values
  language: string;
  quantities: Record<string, number>;

  // Translation function
  t: (key: string) => string;

  // Handlers
  handleQuantityChange: (id: string, change: number) => void;
  handleCompleteSale: () => void;
  handleCustomerServiceComplete: (customerData: Customer, serviceOption: string, appointmentDetails?: any) => Promise<void>;
  handleDeleteClick: (deviceId: string) => void;
  handleConfirmDelete: () => void;
  handleCancelDelete: () => void;
  handleOpenContactModal: () => void;
  handleCloseContactModal: () => void;
  handleContactFormChange: (field: string, value: string) => void;
  handleContactFormSubmit: (e: React.FormEvent) => Promise<void>;
  handleNavigateToSuccessPage: (orderNum: string, details?: any) => void;
  handleBackToHome: () => void;
  handleBackFromCustomerService: () => void;
  goToPrevDevice: () => void;
  goToNextDevice: () => void;
}

/**
 * Custom hook for CartPage business logic
 * Extracts all state management, effects, and handlers from CartPage component
 */
export const useCartPage = ({
  items,
  onRemoveItem,
  onSuccessNavigate
}: UseCartPageProps): UseCartPageReturn => {
  // Page state
  const [showCustomerServicePage, setShowCustomerServicePage] = useState(false);
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<string | null>(null);

  // Contact modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [closingContactModal, setClosingContactModal] = useState(false);
  const [contactForm, setContactForm] = useState<ContactFormState>({
    name: '',
    email: '',
    phone: '',
    message: '',
    contactPreference: 'email'
  });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [showContactSuccess, setShowContactSuccess] = useState(false);

  // Calendar event state
  const [isCreatingCalendarEvent, setIsCreatingCalendarEvent] = useState(false);
  const [calendarEventStatus, setCalendarEventStatus] = useState<CalendarEventStatus>('idle');
  const [calendarEventMessage, setCalendarEventMessage] = useState('');

  // Appointment details state
  const [appointmentDetails, setAppointmentDetails] = useState<AppointmentDetails | null>(null);

  // Device navigation state
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);

  // Context hooks
  const { language } = useLanguage();
  const { goToHome } = useContext(NavigationContext);
  const { quantities, updateQuantity } = useContext(CartContext);
  const { setIsModalOpen } = useContext(ModalContext);

  // Refs
  const pageTopRef = useRef<HTMLDivElement>(null);

  // Translation function
  const t = useCallback((key: string): string => {
    return getTranslation(language as Language, key);
  }, [language]);

  // Scroll to top when switching between sections
  useEffect(() => {
    if (showCustomerServicePage || showSuccessPage) {
      const smoothScrollToTop = () => {
        if (pageTopRef.current) {
          pageTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        // Extra nudge in case content renders after first scroll
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 150);
      };
      const timer = setTimeout(smoothScrollToTop, 50);
      return () => clearTimeout(timer);
    }
  }, [showCustomerServicePage, showSuccessPage]);

  // Auto-redirect to homepage when cart becomes empty
  useEffect(() => {
    if (items.length === 0 && !showCustomerServicePage && !showSuccessPage) {
      goToHome();
    }
  }, [items.length, showCustomerServicePage, showSuccessPage, goToHome]);

  // Reset device index if it's out of bounds
  useEffect(() => {
    if (currentDeviceIndex >= items.length && items.length > 0) {
      setCurrentDeviceIndex(items.length - 1);
    }
  }, [items.length, currentDeviceIndex]);

  // Computed values
  const hasOpAanvraagItem = items.some(item => item.price === -1 || item.price === 0);

  const total = items.reduce((sum, item) => {
    if (item.price === -1 || item.price === 0) {
      return sum;
    }
    return sum + (item.price * (quantities[item.id] || 1));
  }, 0);

  // Handlers
  const handleQuantityChange = useCallback((id: string, change: number) => {
    const newQuantity = Math.max(1, (quantities[id] || 1) + change);
    updateQuantity(id, newQuantity);
  }, [quantities, updateQuantity]);

  const handleCompleteSale = useCallback(() => {
    setShowCustomerServicePage(true);
    // Ensure immediate scroll to top as we transition to Finalize booking
    const forceTop = () => {
      try {
        if (pageTopRef.current) {
          pageTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Fallback for some browsers
        (document.scrollingElement || document.documentElement).scrollTop = 0;
      } catch { /* ignore scroll errors */ }
    };
    // Run after state update and rendering
    requestAnimationFrame(() => {
      forceTop();
      setTimeout(forceTop, 120);
      setTimeout(forceTop, 320);
    });
  }, []);

  const handleNavigateToSuccessPage = useCallback((orderNum: string, details?: any) => {
    setOrderNumber(orderNum);

    // Enhance appointment details with customer information if available
    if (details && selectedCustomer) {
      details.customerName = {
        firstName: selectedCustomer.personalInfo.firstName,
        lastName: selectedCustomer.personalInfo.lastName
      };
    }

    if (details) {
      setAppointmentDetails(details);
    }

    setShowSuccessPage(true);

    // If the parent component provided onSuccessNavigate, call it
    if (onSuccessNavigate) {
      onSuccessNavigate(orderNum, selectedCustomer?.personalInfo?.email || "", details);
    }
  }, [selectedCustomer, onSuccessNavigate]);

  const handleCustomerServiceComplete = useCallback(async (
    customerData: Customer,
    serviceOption: string,
    appointmentDetailsArg?: any
  ) => {
    setSelectedCustomer(customerData);

    // Create order number
    const orderNum = 'PB' + Date.now().toString().slice(-6);

    // Prepare appointment details
    const details = {
      method: serviceOption,
      location: serviceOption === 'store' ? (appointmentDetailsArg?.location || t('physicalStoreLocation')) : t('homePickupLocation'),
      date: appointmentDetailsArg?.date || new Date().toLocaleDateString(getLocaleForLanguage(language)),
      time: appointmentDetailsArg?.time || '10:00',
      customerName: {
        firstName: customerData.personalInfo.firstName,
        lastName: customerData.personalInfo.lastName
      },
      ...appointmentDetailsArg
    };

    // Create Google Calendar event + send email via backend
    try {
      setIsCreatingCalendarEvent(true);
      setCalendarEventStatus('idle');
      setCalendarEventMessage('');

      // No external booking service available - go directly to success page
      setIsCreatingCalendarEvent(false);
      setShowCustomerServicePage(false);
      handleNavigateToSuccessPage(orderNum, details);
    } catch (err) {
      setCalendarEventStatus('error');
      setCalendarEventMessage(t('calendarServiceError'));
      setIsCreatingCalendarEvent(false);
    }
  }, [handleNavigateToSuccessPage, t, language]);

  const handleDeleteClick = useCallback((deviceId: string) => {
    setDeviceToDelete(deviceId);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deviceToDelete) {
      onRemoveItem(deviceToDelete);
    }
    setShowDeleteModal(false);
    setDeviceToDelete(null);
  }, [deviceToDelete, onRemoveItem]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setDeviceToDelete(null);
  }, []);

  const handleOpenContactModal = useCallback(() => {
    setShowContactModal(true);
  }, []);

  const handleCloseContactModal = useCallback(() => {
    setClosingContactModal(true);
    setTimeout(() => {
      setShowContactModal(false);
      setClosingContactModal(false);
      setIsSubmittingContact(false);
      setShowContactSuccess(false);
      setContactForm({
        name: '',
        email: '',
        phone: '',
        message: '',
        contactPreference: 'email'
      });
    }, 300);
  }, []);

  const handleContactFormChange = useCallback((field: string, value: string) => {
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleContactFormSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingContact(true);

    try {
      // Simulate API call with a delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Show success state
      setShowContactSuccess(true);
      setIsSubmittingContact(false);

      // Auto-close after 3 seconds
      setTimeout(() => {
        handleCloseContactModal();
      }, 3000);

    } catch (error) {
      alert(t('genericError'));
      setIsSubmittingContact(false);
    }
  }, [t, handleCloseContactModal]);

  const handleBackToHome = useCallback(() => {
    goToHome();
  }, [goToHome]);

  const handleBackFromCustomerService = useCallback(() => {
    setShowCustomerServicePage(false);
  }, []);

  const goToPrevDevice = useCallback(() => {
    setCurrentDeviceIndex(prev =>
      prev === 0 ? items.length - 1 : prev - 1
    );
  }, [items.length]);

  const goToNextDevice = useCallback(() => {
    setCurrentDeviceIndex(prev =>
      prev === items.length - 1 ? 0 : prev + 1
    );
  }, [items.length]);

  return {
    // State
    showCustomerServicePage,
    showSuccessPage,
    orderNumber,
    selectedCustomer,
    showDeleteModal,
    deviceToDelete,
    showContactModal,
    closingContactModal,
    contactForm,
    isSubmittingContact,
    showContactSuccess,
    isCreatingCalendarEvent,
    calendarEventStatus,
    calendarEventMessage,
    appointmentDetails,
    currentDeviceIndex,

    // Computed values
    hasOpAanvraagItem,
    total,

    // Refs
    pageTopRef,

    // Context values
    language,
    quantities,

    // Translation function
    t,

    // Handlers
    handleQuantityChange,
    handleCompleteSale,
    handleCustomerServiceComplete,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    handleOpenContactModal,
    handleCloseContactModal,
    handleContactFormChange,
    handleContactFormSubmit,
    handleNavigateToSuccessPage,
    handleBackToHome,
    handleBackFromCustomerService,
    goToPrevDevice,
    goToNextDevice,
  };
};

export default useCartPage;
